import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Overlay, { Placement } from 'react-bootstrap2/Overlay';
import { popperModifiers } from '__components/helpers';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CHAT_BOT } from '__queries/query-keys';
import { ChatBot } from 'ts-components/ChatBot';
import { OutputVariableRequirements } from 'ts-components/ChatBot/types';
import { useGenerationFlowEventStream } from 'ts-components/Conversation/useGenerationFlowEventStream';
import { toastVariant, useToast } from '__components/ToastProvider';
import MachineLearningService from 'services/MachineLearningService';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { layers } from '__app/spacing';
import KizenAILogo from '../AiSinglePromptPopover/assets/kizen-ai.svg?react';
import { Typography } from '@kizen/kds/Typography';

export type FlowStatusData<TRunMetadata = any> = {
  status?: string;
  run_metadata?: TRunMetadata;
  session_id?: string;
};

export type NextAction = {
  metadata?: any;
  url?: string;
  label?: string;
};

export type ChatSessionFlowStatusData = {
  next_actions: NextAction[];
  message?: string;
};

type GenerationStatus =
  | 'idle'
  | 'generating'
  | 'creating-session'
  | 'completed'
  | 'failed';

export const GENERATION_STATUS = {
  IDLE: 'idle',
  GENERATING: 'generating',
  CREATING_SESSION: 'creating-session',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type GenerationResult = {
  hasResult: boolean;
  sessionId?: string;
  guardrailCode?: string;
  guardrailMessage?: string;
  shouldNavigate?: boolean;
  navigationUrl?: string;
  navigationGroupId?: string;
};

type AiExpandableSinglePromptProps = {
  flowType: string;
  taskType?: string;
  customObjectId?: string;
  dashboardId?: string;
  builderScope?: 'homepage' | 'dashboard' | 'charts';
  context: Record<string, any>;
  mode: 'create' | 'edit';
  importResults?: boolean;
  onOneShotSuccess?: (flowStatusData: FlowStatusData) => void;
  onChatSessionSuccess?: (flowStatusData: ChatSessionFlowStatusData) => void;
  onGenerationFailure: (error: any) => void;
  entityName?: string;
  extractGenerationResult: (response: FlowStatusData) => GenerationResult;
  setIsGenerating: (isGenerating: boolean) => void;
  messagesConfig?: {
    placeholder?: string;
    buildingMessage?: string;
    successMessage?: string;
    failureMessage?: string;
    expandedPlaceholder?: string;
    closingMessage?: string;
    initialMessage?: string;
  };
  placeholder?: string;
  inModal?: boolean;
  autoOpen?: boolean;
  autoOpenDelay?: number;
  expandInPlace?: boolean;
  popoverProps?: {
    placement?: Placement;
    container?: HTMLElement | null;
    rootClose?: boolean;
    show?: boolean;
    zIndex?: number;
  };
  buttonLabel?: string;
  oneShotOnly?: boolean;
};

export const AiExpandableSinglePrompt = ({
  flowType,
  taskType = 'filter_plan_generation',
  customObjectId,
  dashboardId,
  builderScope,
  context,
  onOneShotSuccess,
  onChatSessionSuccess,
  onGenerationFailure,
  entityName = 'Entity',
  extractGenerationResult,
  messagesConfig = {},
  inModal,
  autoOpen = false,
  autoOpenDelay = 1000,
  expandInPlace,
  popoverProps,
  mode,
  importResults,
  setIsGenerating = () => {},
  buttonLabel,
  oneShotOnly = false,
}: AiExpandableSinglePromptProps) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const queryClient = useQueryClient();

  const { data: modelConfigMetadata } = useQuery({
    queryKey: CHAT_BOT.METADATA,
    queryFn: async () => {
      return MachineLearningService.getChatMetadata();
    },
    enabled: true,
  });

  const [show, setShow] = useState(false);
  const [chatSessionId, setChatSessionId] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [status, setStatus] = useState<GenerationStatus>(
    GENERATION_STATUS.IDLE
  );
  const [pendingMessageToSend, setPendingMessageToSend] = useState('');
  const [dynamicHeight, setDynamicHeight] = useState<number | null>(null);
  const [maxAvailableHeight, setMaxAvailableHeight] = useState<number | null>(
    null
  );
  const [failedErrorMessage, setFailedErrorMessage] = useState('');
  const [escalated, setEscalated] = useState(false);
  const [escalatedByError, setEscalatedByError] = useState(false);
  const [escalatedSessionFetched, setEscalatedSessionFetched] = useState(false);
  const [isOneShotGeneration, setIsOneShotGeneration] = useState(false);

  useEffect(() => {
    if (autoOpen) {
      if (autoOpenDelay > 0) {
        setTimeout(() => setShow(true), autoOpenDelay);
      } else {
        setShow(true);
      }
    }
  }, [autoOpen, autoOpenDelay]);

  const buttonRef = useRef(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const messageHistoryRef = useRef<HTMLDivElement>(null);
  const hasResizedOnce = useRef(false);
  const sessionFetchedRef = useRef(false);

  const hasGeneratedOnce = status !== GENERATION_STATUS.IDLE;
  const isGenerating = status === GENERATION_STATUS.GENERATING;
  const isCompleted = status === GENERATION_STATUS.COMPLETED;
  const isCreatingSession = status === GENERATION_STATUS.CREATING_SESSION;

  const flagGenerationError = useCallback(
    (
      sessionId: string | undefined,
      flowId: string | undefined,
      reason: string
    ) => {
      if (sessionId) {
        MachineLearningService.createChatFlag({
          chatId: sessionId,
          tag: 'error',
          reason,
          options: {},
        }).catch((error) => {
          monitoringExceptionHelper(error);
        });
      }
      if (flowId) {
        MachineLearningService.createFlowFlag({
          flowId,
          tag: 'error',
          reason,
          options: {},
        }).catch((error) => {
          monitoringExceptionHelper(error);
        });
      }
    },
    []
  );

  const streamEventListeners = useMemo(() => {
    const eventListenerNames =
      modelConfigMetadata?.[taskType]?.output_variables?.map(
        ({ name }: OutputVariableRequirements) => name
      ) ?? [];
    return eventListenerNames;
  }, [modelConfigMetadata, taskType]);

  const resetState = useCallback(() => {
    const currentSessionId = chatSessionId;
    setChatSessionId('');
    setIsExpanded(false);
    setStatus(GENERATION_STATUS.IDLE);
    setPendingMessageToSend('');
    setDynamicHeight(null);
    setFailedErrorMessage('');
    setEscalated(false);
    setEscalatedByError(false);
    setEscalatedSessionFetched(false);
    setIsOneShotGeneration(false);
    setIsGenerating?.(false);
    hasResizedOnce.current = false;
    sessionFetchedRef.current = false;

    // Clear the query cache for this session to prevent stale data
    if (currentSessionId) {
      queryClient.removeQueries({
        queryKey: CHAT_BOT.CHAT_SESSION(currentSessionId),
      });
    }
  }, [chatSessionId, queryClient, setIsGenerating]);

  const { startFlowStream } = useGenerationFlowEventStream({
    chatSessionId,
    importResults,
    onGenerationStart: useCallback(
      () => setStatus(GENERATION_STATUS.GENERATING),
      []
    ),
    onSuccess: useCallback(
      (data: any) => {
        setIsGenerating?.(false);
        setStatus(GENERATION_STATUS.COMPLETED);
        onChatSessionSuccess?.(data);
        // delay before animated collapse
        setTimeout(() => {
          setShow(false);
          resetState();
        }, 1500);
      },
      [onChatSessionSuccess, resetState, setIsGenerating]
    ),
  });

  const handleStartGeneration = useCallback(
    async (prompt: string) => {
      try {
        setIsOneShotGeneration(true);
        setIsGenerating?.(true);
        setStatus(GENERATION_STATUS.GENERATING);
        const response = await MachineLearningService.startFlow({
          type: flowType,
          user_request: prompt,
          target_object_id: customObjectId,
          target_dashboard_id: dashboardId,
          run_context: {
            ...context,
            ...(builderScope && { builder_scope: builderScope }),
            ...(mode && { mode: mode }),
          },
          options: {
            in_sync: true,
            import_result: importResults,
          },
        });

        const result = extractGenerationResult(response);
        if (result.sessionId) {
          setIsOneShotGeneration(false);
          setEscalated(true);
          setEscalatedByError(true);
          setChatSessionId(result.sessionId);
          setIsExpanded(true);
          setStatus(GENERATION_STATUS.IDLE);
          setIsGenerating?.(false);
          return;
        } else {
          setEscalated(false);
          setEscalatedByError(false);
        }

        if (result.hasResult) {
          setIsOneShotGeneration(false);
          onOneShotSuccess?.(response);
          setStatus(GENERATION_STATUS.COMPLETED);
          setIsGenerating?.(false);
          // delay before animated collapse
          setTimeout(() => {
            setShow(false);
            resetState();
          }, 1500);
          return;
        }

        if (!result.hasResult && result.guardrailMessage) {
          showToast({
            message: result.guardrailMessage,
            variant: toastVariant.FAILURE,
          });
        } else {
          onGenerationFailure(response);
        }

        const failureMsg =
          messagesConfig.failureMessage ??
          (t('Failed to generate {{entityName}}, please try again.', {
            entityName: entityName.toLowerCase(),
          }) as string);
        setIsOneShotGeneration(false);
        setStatus(GENERATION_STATUS.FAILED);
        setFailedErrorMessage(failureMsg);
        onGenerationFailure(response);
        flagGenerationError(
          response.session_id,
          response.flow_id || response.id,
          'One-shot generation returned no result'
        );
        setIsGenerating?.(false);
        // delay before animated collapse
        setTimeout(() => {
          resetState();
        }, 1500);
      } catch (error: any) {
        setIsOneShotGeneration(false);
        setIsGenerating?.(false);
        setStatus(GENERATION_STATUS.FAILED);
        const usageError = error?.response?.data?.run_context?.usage;
        const errorMsg =
          usageError?.[0] ??
          messagesConfig.failureMessage ??
          (t('{{entityName}} Generation Failed, Please try again later', {
            entityName,
          }) as string);

        setFailedErrorMessage(errorMsg);
        onGenerationFailure(error);
        const errorSessionId = error?.response?.data?.session_id;
        const errorFlowId =
          error?.response?.data?.flow_id || error?.response?.data?.id;
        if (errorSessionId || errorFlowId) {
          flagGenerationError(
            errorSessionId,
            errorFlowId,
            'One-shot generation failed with exception'
          );
        }
      }
    },
    [
      dashboardId,
      flagGenerationError,
      importResults,
      mode,
      setIsGenerating,
      flowType,
      customObjectId,
      builderScope,
      context,
      extractGenerationResult,
      onOneShotSuccess,
      onGenerationFailure,
      messagesConfig,
      t,
      entityName,
      resetState,
      showToast,
    ]
  );

  // Calculate dynamic height based on MessageHistory, we want to expand to show full user message if possible
  useEffect(() => {
    if (!hasGeneratedOnce || isExpanded || hasResizedOnce.current) return;

    const messageHistoryElement = messageHistoryRef.current;
    if (!messageHistoryElement) return;

    let resizeObserver: ResizeObserver | null = null;

    resizeObserver = new ResizeObserver(() => {
      if (hasResizedOnce.current) return;

      const TEXTAREA_HEIGHT = 50;
      const QUICK_ACTIONS_HEIGHT = 60;
      const PADDING = 20;
      const MIN_HEIGHT = 200;
      const MAX_HEIGHT = 600;

      const calculatedHeight =
        messageHistoryElement.scrollHeight +
        TEXTAREA_HEIGHT +
        PADDING +
        QUICK_ACTIONS_HEIGHT;
      const clampedHeight = Math.min(
        Math.max(calculatedHeight, MIN_HEIGHT),
        MAX_HEIGHT
      );

      setDynamicHeight(clampedHeight);
      hasResizedOnce.current = true;

      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    });

    resizeObserver.observe(messageHistoryElement);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [hasGeneratedOnce, isExpanded]);

  // Close overlay when button becomes invisible (collapsed, scrolled, etc.)
  useEffect(() => {
    if (!buttonRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && (show || isExpanded)) {
          setShow(false);
        }
      },
      { threshold: 0, rootMargin: '100px' }
    );

    observer.observe(buttonRef.current);
    return () => observer.disconnect();
  }, [show, isExpanded, chatSessionId, resetState]);

  // Calculate max available height based on viewport when overlay opens
  useEffect(() => {
    if (!show || !buttonRef.current) {
      setMaxAvailableHeight(null);
      return;
    }

    const button = buttonRef.current as HTMLElement;
    const rect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const VIEWPORT_PADDING = 20;

    const availableBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;

    setMaxAvailableHeight(Math.max(availableBelow, 200));
  }, [show]);

  const overlayContainerStyles = useMemo(() => {
    const heightStyle =
      !isExpanded && dynamicHeight ? { height: `${dynamicHeight}px` } : {};

    const maxHeightStyle = maxAvailableHeight
      ? { maxHeight: `${maxAvailableHeight}px` }
      : {};

    return {
      ...heightStyle,
      ...maxHeightStyle,
      transition: 'all 600ms',
      transitionDelay: '300ms',
      transitionProperty: 'height',
    };
  }, [isExpanded, dynamicHeight, maxAvailableHeight]);

  const computedPlaceholder = useMemo(() => {
    if (isCompleted) {
      return messagesConfig.closingMessage || 'Closing session';
    }
    if (isGenerating) {
      return (
        messagesConfig.buildingMessage ||
        t('{{entityName}} is Building...', { entityName })
      );
    }
    if (isExpanded) {
      return 'Add Additional Context';
    }
    return messagesConfig.placeholder || t('What would you like to build?');
  }, [isCompleted, isGenerating, isExpanded, messagesConfig, t, entityName]);

  const disableTextAreaMessage = useMemo(() => {
    if (isGenerating || isCreatingSession) {
      return (
        messagesConfig.buildingMessage ||
        t('Building your {{entityName}}...', {
          entityName: entityName.toLowerCase(),
        })
      );
    }
    return undefined;
  }, [isGenerating, isCreatingSession, messagesConfig, t, entityName]);

  const mockSession = useMemo(() => {
    // When escalated (one-shot returned a session), hide mock once session is fetched
    if (escalatedSessionFetched && chatSessionId) {
      return undefined;
    }

    // Otherwise, keep showing mock until pendingMessageToSend is cleared
    if (chatSessionId && sessionFetchedRef.current && !pendingMessageToSend) {
      return undefined;
    }

    const messages = [];

    messages.push({
      id: 'initial-message',
      isCollapsible: false,
      isResponseLoading: false,
      isUserMessage: false,
      messageContent: messagesConfig.initialMessage,
    });

    if (pendingMessageToSend) {
      messages.push({
        id: 'pending-message',
        isCollapsible: false,
        isResponseLoading: false,
        isUserMessage: true,
        messageContent: pendingMessageToSend,
      });
    }

    if (
      (status === GENERATION_STATUS.GENERATING ||
        status === GENERATION_STATUS.CREATING_SESSION) &&
      pendingMessageToSend
    ) {
      messages.push({
        id: 'loading',
        messageContent:
          status === GENERATION_STATUS.CREATING_SESSION || isExpanded
            ? ''
            : messagesConfig.buildingMessage ||
              t('Building your {{entityName}}', {
                entityName: entityName.toLowerCase(),
              }),
        isUserMessage: false,
        isResponseLoading: true,
        isCollapsible: false,
      });
    } else if (status === GENERATION_STATUS.COMPLETED) {
      messages.push({
        id: 'completed',
        messageContent:
          messagesConfig.successMessage ||
          t('{{entityName}} Built Successfully', { entityName }),
        isUserMessage: false,
        isResponseLoading: false,
        isCollapsible: false,
      });
    } else if (status === GENERATION_STATUS.FAILED) {
      if (failedErrorMessage) {
        messages.push({
          id: 'failed-error',
          messageContent: failedErrorMessage,
          isUserMessage: false,
          isResponseLoading: false,
          isCollapsible: false,
          isErrorMessage: true,
        });
      } else {
        messages.push({
          id: 'failed',
          messageContent: t('Failed'),
          isUserMessage: false,
          isResponseLoading: false,
          isCollapsible: false,
        });
      }
    }

    return messages;
  }, [
    chatSessionId,
    pendingMessageToSend,
    status,
    failedErrorMessage,
    isExpanded,
    messagesConfig,
    t,
    entityName,
    escalatedSessionFetched,
  ]);

  const handleFetchSession = useCallback(async () => {
    if (chatSessionId) {
      const result = await MachineLearningService.getChatHistory(chatSessionId);
      if (escalated && !escalatedSessionFetched) {
        setEscalatedSessionFetched(true);
      }
      return result;
    }

    return { messages: [] };
  }, [chatSessionId, escalated, escalatedSessionFetched]);

  const handleChatButtonClick = useCallback(
    async (message: string) => {
      try {
        setPendingMessageToSend(message);

        setIsExpanded(true);

        const session = await MachineLearningService.createNewChatSession(
          taskType,
          {
            ...context,
            custom_object_id: customObjectId,
            ...(builderScope && { builder_scope: builderScope }),
          }
        );
        setChatSessionId(session.id);
        setEscalated(true);
        if (message === '') {
          setStatus(GENERATION_STATUS.IDLE);
        }
      } catch (error: any) {
        setStatus(GENERATION_STATUS.IDLE);
        const errorData = error?.response?.data;
        showToast({
          message:
            errorData?.message ||
            errorData?.context?.usage?.[0] ||
            t('Failed to create chat session'),
          variant: toastVariant.FAILURE,
        });
        // delay before animated collapse
        setTimeout(() => {
          resetState();
        }, 1500);
      }
    },
    [taskType, customObjectId, builderScope, showToast, context, resetState, t]
  );

  const handleMockedSessionInteraction = useCallback(
    async (messageToSend: string, isResetting?: boolean) => {
      if (isCreatingSession) {
        return;
      }

      setPendingMessageToSend(messageToSend);
      if (!chatSessionId && !isResetting) {
        await handleStartGeneration(messageToSend);
      }
    },
    [chatSessionId, isCreatingSession, handleStartGeneration]
  );

  const handleCompleteSession = useCallback(
    async (sessionId: string) => {
      setIsGenerating?.(true);
      await startFlowStream(sessionId);
    },
    [startFlowStream, setIsGenerating]
  );

  const handleDeleteSession = useCallback(async () => {
    if (chatSessionId) {
      await MachineLearningService.cancelChatSession(chatSessionId);
    }
  }, [chatSessionId]);

  const computedZIndex = useMemo(() => {
    if (popoverProps?.zIndex !== undefined) {
      return popoverProps.zIndex;
    }
    if (expandInPlace) {
      return 1;
    }
    return inModal ? layers.modals(0, 1) : 0;
  }, [popoverProps?.zIndex, expandInPlace, inModal]);

  return (
    <div
      ref={wrapperRef}
      className={expandInPlace ? 'relative inline-block' : 'inline-block'}
    >
      <button
        ref={buttonRef}
        className="bg-transparent border-none text-button-secondary-default hover:text-button-secondary-hover flex items-center gap-[10px]"
        onClick={() => {
          setShow(!show);
        }}
      >
        <KizenAILogo className="h-[30px] w-[30px]" />
        {buttonLabel && (
          <Typography weight="bold" size="sm">
            {buttonLabel}
          </Typography>
        )}
      </button>

      <Overlay
        transition={false}
        target={buttonRef.current}
        show={show}
        placement={popoverProps?.placement || 'bottom-start'}
        onHide={() => setShow(false)}
        rootClose={popoverProps?.rootClose ?? true}
        container={expandInPlace ? wrapperRef.current : popoverProps?.container}
        popperConfig={{
          modifiers: [
            popperModifiers.addStyles({
              zIndex: computedZIndex,
            }),
          ],
        }}
      >
        <div
          className={`
          flex flex-col relative w-[400px] overflow-hidden bg-background-white border-border-medium border-1 border-solid rounded-[4px] p-[0] shadow-tile overflow-visible transition-all duration-600 delay-250
          ${isExpanded ? 'h-[400px] w-[402px]' : !dynamicHeight ? 'h-[250px]' : ''}
        `}
          style={overlayContainerStyles}
        >
          <ChatBot
            entityName={entityName}
            chatSessionId={chatSessionId}
            fetchSession={handleFetchSession}
            onCompleteSession={handleCompleteSession}
            getStreamingEndpoint={(id: string) =>
              MachineLearningService.getStreamingEndpoint(id)
            }
            onDeleteSession={handleDeleteSession}
            eventListenerNames={streamEventListeners}
            enableQuickActionButtons
            hideQuickActionsButtons={
              Boolean(mockSession) &&
              !isOneShotGeneration &&
              failedErrorMessage === ''
            }
            alwaysShowStartOver={escalated || failedErrorMessage !== ''}
            onInitialMessageLoading={() => {
              setStatus(GENERATION_STATUS.IDLE);
              setPendingMessageToSend('');
            }}
            hideScrollbar={Boolean(mockSession)}
            onStartOver={resetState}
            disabledSend={isCreatingSession || isGenerating}
            disableMessageAnimation={pendingMessageToSend.length > 0}
            mockSession={mockSession}
            onMockedSessionInteraction={handleMockedSessionInteraction}
            showChatButton={!isExpanded}
            showOneShotGenerationButton={!isExpanded}
            oneShotOnly={oneShotOnly}
            clearMessageQueue={
              !escalatedByError && escalated && pendingMessageToSend === ''
            }
            skipMessageQueue={escalatedByError && pendingMessageToSend !== ''}
            onChatButtonClick={handleChatButtonClick}
            placeholderOverride={computedPlaceholder}
            headerMenuOptions={
              isExpanded ? [{ value: 'close', label: t('Close') }] : undefined
            }
            messageHistoryRef={messageHistoryRef}
            shouldFocusInput={show && !isCreatingSession && !isGenerating}
            forceSessionRefetch={
              !!(chatSessionId && !sessionFetchedRef.current)
            }
            stopRefetchAfterDelete
            disableSessionRefetch={() => {
              sessionFetchedRef.current = true;
            }}
            showFixedHeight={Boolean(mockSession)}
            disableTextAreaMessage={disableTextAreaMessage}
          />
        </div>
      </Overlay>
    </div>
  );
};
